'use client';

import { Minus, Plus } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface WindowQuantityStepperProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  disabled?: boolean;
}

export function WindowQuantityStepper({
  label,
  value,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  className,
  disabled = false,
}: WindowQuantityStepperProps) {
  const canDecrement = !disabled && value > min;
  const canIncrement = !disabled && value < max;

  return (
    <div
      className={cn(
        'flex w-full items-center justify-center gap-4',
        disabled && 'opacity-60',
        className,
      )}
    >
      <button
        type='button'
        onClick={() => onChange(Math.max(min, Math.round((value - step) * 100) / 100))}
        disabled={!canDecrement}
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-orange-peel/40 transition-colors',
          canDecrement
            ? 'text-oxford-blue hover:border-orange-peel hover:bg-orange-peel/5'
            : 'cursor-not-allowed border-orange-peel/15 text-oxford-blue/25',
        )}
        aria-label={`${label}: decrease`}
      >
        <Minus className='h-4 w-4' strokeWidth={2.5} aria-hidden />
      </button>
      <div className='flex min-w-0 flex-1 items-center justify-center text-center'>
        <span className='truncate text-sm font-semibold tabular-nums text-oxford-blue sm:text-base'>
          {label}
        </span>
      </div>
      <button
        type='button'
        onClick={() => onChange(Math.min(max, Math.round((value + step) * 100) / 100))}
        disabled={!canIncrement}
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white transition-colors',
          canIncrement
            ? 'bg-orange-peel hover:bg-orange-peel/90'
            : 'cursor-not-allowed bg-orange-peel/30 text-white/60',
        )}
        aria-label={`${label}: increase`}
      >
        <Plus className='h-4 w-4' strokeWidth={2.5} aria-hidden />
      </button>
    </div>
  );
}
